import { jsx as _jsx } from "react/jsx-runtime";
/**
 * Commerce motion (BRAND.md §4.10): the small pieces that make adding to a cart, changing a quantity, paying and
 * failing feel immediate. All are CSS transforms and opacity (plus one number tween); none loops.
 *
 * Every piece asks one question first, `motionOn()`: is `--clavian-motion-scale` above zero where it is drawn? It is 0
 * under `prefers-reduced-motion` and under `data-motion="none"` (the kiosk during a session), and it is unset where no
 * stylesheet is loaded (a test), which counts as off. When it is off every piece lands on its resting frame at once:
 * the number is simply the new number, no token flies, an item that is removed is gone.
 */
import { useEffect, useRef, useState, } from 'react';
import { cx } from '../lib/cx.js';
/** Is motion on for `element` (the document when omitted)? Unset, zero or unreadable all mean off. */
export function motionOn(element) {
    if (typeof window === 'undefined' || typeof window.getComputedStyle !== 'function')
        return false;
    try {
        const raw = window.getComputedStyle(element ?? document.documentElement).getPropertyValue('--clavian-motion-scale').trim();
        const scale = Number.parseFloat(raw);
        return Number.isFinite(scale) && scale > 0;
    }
    catch {
        return false;
    }
}
/** Replay a one-shot CSS animation class on an element (remove, reflow, add; removed again when it ends). */
export function replayClass(element, className) {
    if (element === null || element === undefined || !motionOn(element))
        return;
    element.classList.remove(className);
    void element.offsetWidth;
    element.classList.add(className);
    const done = () => {
        element.classList.remove(className);
        element.removeEventListener('animationend', done);
    };
    element.addEventListener('animationend', done);
}
/** The attribute a cart's count (or total) carries so a token knows where to fly. */
export const CART_TARGET_ATTR = 'data-cart-target';
/**
 * A small token (a dot in the action colour) flies from `from` to the cart: a CSS transform on a fixed-position
 * element, 420 ms by default (never more than 450), removed when it lands. Nothing happens when motion is off, when
 * the cart is not on screen, or where the Web Animations API is missing.
 */
export function flyToken(from, options = {}) {
    if (from === null || from === undefined || typeof document === 'undefined')
        return;
    const target = options.to ?? document.querySelector(`[${CART_TARGET_ATTR}]`);
    if (target === null || !motionOn(from))
        return;
    const start = from.getBoundingClientRect();
    const end = target.getBoundingClientRect();
    if (end.width === 0 && end.height === 0)
        return;
    const token = document.createElement('span');
    token.className = 'clav-fly-token';
    token.setAttribute('aria-hidden', 'true');
    token.style.left = `${start.left + start.width / 2 - 9}px`;
    token.style.top = `${start.top + start.height / 2 - 9}px`;
    document.body.appendChild(token);
    const dx = end.left + end.width / 2 - (start.left + start.width / 2);
    const dy = end.top + end.height / 2 - (start.top + start.height / 2);
    const remove = () => token.remove();
    if (typeof token.animate !== 'function') {
        remove();
        return;
    }
    const animation = token.animate([
        { transform: 'translate(0, 0) scale(1)', opacity: 1 },
        { transform: `translate(${dx * 0.55}px, ${dy * 0.55 - 36}px) scale(1.1)`, opacity: 1, offset: 0.55 },
        { transform: `translate(${dx}px, ${dy}px) scale(0.4)`, opacity: 0.2 },
    ], { duration: Math.min(options.durationMs ?? 420, 450), easing: 'cubic-bezier(0.22, 0.8, 0.3, 1)', fill: 'forwards' });
    animation.onfinish = remove;
    animation.oncancel = remove;
}
/**
 * Feedback for an Add: the item card pulses and a token flies from the button to the cart. Call it from the click
 * handler with the button and the card; the cart count bumps by itself through `useBumpKey`.
 */
export function addToCartFeedback(button, card) {
    replayClass(card ?? button?.closest('[data-pulse-card]'), 'clav-pulse');
    flyToken(button);
}
/** A counter that goes up whenever `value` changes after the first render: use it as a React `key` to replay a bump. */
export function useBumpKey(value) {
    const [bump, setBump] = useState(0);
    const first = useRef(true);
    const last = useRef(value);
    useEffect(() => {
        if (first.current) {
            first.current = false;
            return;
        }
        if (!Object.is(last.current, value))
            setBump((n) => n + 1);
        last.current = value;
    }, [value]);
    return bump;
}
/** The cart's count badge: it bumps when the number changes. Give the page one, marked as the flight's target. */
export function CartBadge({ count, className, target = true }) {
    const bump = useBumpKey(count);
    const attributes = target ? { [CART_TARGET_ATTR]: '' } : {};
    return (_jsx("span", { className: cx('clav-cart-badge', className), ...attributes, "aria-hidden": "true", children: _jsx("span", { className: "clav-cart-badge__n", "data-bumped": bump > 0 || undefined, "data-zero": count === 0 || undefined, children: count }, bump) }));
}
/**
 * A figure that rolls to its new value: a short number tween, drawn in tabular figures so the width does not jump. The
 * text always settles on exactly `format(value)`; with motion off it is that from the first frame.
 */
export function RollingNumber({ value, format, durationMs = 320, className }) {
    const [shown, setShown] = useState(value);
    const shownRef = useRef(value);
    const host = useRef(null);
    useEffect(() => {
        const from = shownRef.current;
        if (from === value)
            return;
        if (!motionOn(host.current)) {
            shownRef.current = value;
            setShown(value);
            return;
        }
        let frame = 0;
        const startedAt = performance.now();
        const step = (now) => {
            const progress = Math.min(1, (now - startedAt) / durationMs);
            const eased = 1 - (1 - progress) ** 3;
            const next = progress >= 1 ? value : Math.round(from + (value - from) * eased);
            shownRef.current = next;
            setShown(next);
            if (progress < 1)
                frame = requestAnimationFrame(step);
        };
        frame = requestAnimationFrame(step);
        return () => cancelAnimationFrame(frame);
    }, [value, durationMs]);
    return (_jsx("span", { ref: host, className: cx('clav-rolling', className), children: format(shown) }));
}
/**
 * A list that lets a removed item collapse before it goes. It returns the live items plus, for ~200 ms, the ones that
 * were just removed (marked `leaving`) in their old place. With motion off nothing is kept and the list is the list.
 */
export function useCollapsingList(items, keyOf, durationMs = 200) {
    const previous = useRef(items);
    const [ghosts, setGhosts] = useState([]);
    const alive = useRef(true);
    useEffect(() => {
        alive.current = true;
        return () => {
            alive.current = false;
        };
    }, []);
    useEffect(() => {
        const keys = new Set(items.map((item) => keyOf(item)));
        const removed = previous.current
            .map((item, index) => ({ item, index }))
            .filter(({ item }) => !keys.has(keyOf(item)));
        previous.current = items;
        if (removed.length === 0 || !motionOn())
            return;
        setGhosts((current) => [...current, ...removed]);
        setTimeout(() => {
            if (alive.current)
                setGhosts((current) => current.filter((ghost) => !removed.includes(ghost)));
        }, durationMs);
    }, [items, durationMs, keyOf]);
    const entries = items.map((item) => ({ item, key: keyOf(item), leaving: false }));
    const liveKeys = new Set(entries.map((entry) => entry.key));
    for (const ghost of ghosts) {
        const key = keyOf(ghost.item);
        if (liveKeys.has(key))
            continue;
        entries.splice(Math.min(ghost.index, entries.length), 0, { item: ghost.item, key, leaving: true });
    }
    return entries;
}
/**
 * Failure feedback for one field: a gentle shake each time `trigger` goes up. Spread the result on the field's wrapper.
 * Nothing moves when motion is off (the error text is the message).
 */
export function useShake(trigger) {
    return trigger > 0 ? { className: 'clav-shake', key: `shake-${trigger}` } : { key: 'shake-0' };
}
/** A step of a flow (checkout's tender, then paid): new content slides in from the side when `step` changes. */
export function StepSlide({ step, direction = 'forward', className, children }) {
    return (_jsx("div", { className: cx('clav-step-slide', className), "data-direction": direction, children: children }, step));
}
//# sourceMappingURL=commerce-motion.js.map